import type { Point } from './world';
import { isAppearanceCode } from './appearance';

export const NAME_KEYS = ['cloud', 'stream', 'breeze', 'leaf', 'rain', 'sun', 'pine', 'dew', 'moon', 'bamboo', 'flower', 'sky', 'reed', 'pebble', 'river', 'hill', 'lake', 'moss', 'dawn', 'dusk', 'willow', 'wave', 'fern', 'seed', 'mist', 'branch', 'lotus', 'sand', 'rainbow', 'field', 'horizon', 'star'] as const;
export type NameKey = typeof NAME_KEYS[number];
export type Action = 'idle' | 'sit' | 'palms' | 'bow' | 'prostrate' | 'beads';
export type PublicGuest = {
  id: string;
  nameKey: NameKey;
  appearance: number;
  position: Point;
  from: Point;
  movedAt: number;
  arrivesAt: number;
  seatId: string | null;
  action: Action;
  actionUntil: number;
  connected: boolean;
};
export type ClientMessage =
  | { type: 'hello'; resumeToken?: string; appearance?: number }
  | { type: 'look'; appearance: number }
  | { type: 'move'; point: Point }
  | { type: 'sit'; seatId: string }
  | { type: 'action'; action: Action };
export type ErrorCode = 'full' | 'seat_taken' | 'invalid' | 'sit_first' | 'arriving' | 'slow_down' | 'expired' | 'unavailable';
export type ServerMessage =
  | { type: 'welcome'; you: string; resumeToken: string; roomId: string; guests: PublicGuest[]; capacity: number; serverTime: number }
  | { type: 'state'; guests: PublicGuest[]; serverTime: number }
  | { type: 'error'; code: ErrorCode };
export type EventName = 'room_enter' | 'rest_start' | 'rest_three_minutes' | 'rest_complete' | 'return_day';
export const EVENT_NAMES: readonly EventName[] = ['room_enter', 'rest_start', 'rest_three_minutes', 'rest_complete', 'return_day'];
export function isRoomId(value: unknown): value is string {
  return typeof value === 'string' && /^garden-[1-9]\d{0,6}$/.test(value);
}
export function parseClientMessage(value: unknown): ClientMessage | null {
  if (!value || typeof value !== 'object') return null;
  const message = value as Record<string, unknown>;
  // An unknown appearance is ignored, not fatal: the server then assigns a random look.
  if (message.type === 'hello' && (message.resumeToken === undefined || (typeof message.resumeToken === 'string' && message.resumeToken.length <= 100))) {
    return { type: 'hello', ...(typeof message.resumeToken === 'string' ? { resumeToken: message.resumeToken } : {}), ...(isAppearanceCode(message.appearance) ? { appearance: message.appearance } : {}) };
  }
  if (message.type === 'look' && isAppearanceCode(message.appearance)) return { type: 'look', appearance: message.appearance };
  if (message.type === 'move' && message.point && typeof message.point === 'object') {
    const p = message.point as Record<string, unknown>;
    if (typeof p.x === 'number' && typeof p.y === 'number' && Number.isFinite(p.x) && Number.isFinite(p.y)) return { type: 'move', point: { x: p.x, y: p.y } };
  }
  if (message.type === 'sit' && typeof message.seatId === 'string' && message.seatId.length < 40) return { type: 'sit', seatId: message.seatId };
  if (message.type === 'action' && ['idle', 'sit', 'palms', 'bow', 'prostrate', 'beads'].includes(String(message.action))) return { type: 'action', action: message.action as Action };
  return null;
}
